import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from '../../../../config/api';
import RehostModal from "../../../../components/RehostModal.jsx";
import GlobalHeader from "../../../../components/GlobalHeader.jsx";
import EditableCustomerEventCard from "../../EventHost/components/EditableCustomerEventCard.jsx";
import "../css/RehostEventDetails.css";

const RehostEventDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Static header title for this page
  const headerTitle = 'Rehost Event';

  // Event passed from the RehostEventForm via route state
  const event = location.state?.event || null;

  // Build initial form data from the selected event, leaving date/time
  // fields and package sale dates blank so the organiser must choose
  // new dates when rehosting.
  const { formData, packages, tabs } = useMemo(() => {
    if (!event) {
      return {
        formData: {},
        packages: [],
        tabs: [],
      };
    }

    const baseFormData = {
      name: event.event_name || event.event_name === '' ? event.event_name : '',
      location: event.location || '',
      // Pre-fill date/time fields from the original event so they are visible
      startdate: event.start_date || event.date || event.startdate || '',
      enddate: event.end_date || event.enddate || event.start_date || '',
      start_time: event.start_time || event.time || '',
      end_time: event.end_time || event.endtime || '',
      deadlinedate: event.deadlinedate || event.start_date || event.date || '',
      deadlinetime: event.deadlinetime || event.start_time || event.time || '',
      type: event.event_type || event.type || '',
      capacity: event.total_tickets_planned || event.capacity || '',
      description: event.description || '',
      terms_and_conditions: event.terms_and_conditions || '',
      duration: '',
    };

    const mappedPackages = Array.isArray(event.packages)
      ? event.packages.map((pkg) => ({
          selectType: 'Full Package',
          packageType: pkg.name || '',
          location: event.location || '',
          duration: pkg.duration || '',
          // Clear sale dates so organiser can pick new ones
          startDate: '',
          endDate: '',
          pricing: pkg.price != null ? String(pkg.price) : '',
          details: pkg.description || '',
          typeOptions: ['Full Package', 'Day'],
        }))
      : [];

    const mappedTabs = Array.isArray(event.tab_sections)
      ? event.tab_sections.map((tab) => ({
          name: tab.name || '',
          content: tab.content || '',
        }))
      : [];

    return {
      formData: baseFormData,
      packages: mappedPackages,
      tabs: mappedTabs,
    };
  }, [event]);

  // Local editable state for the shell, initialised from the mapped values
  const [formState, setFormState] = useState(formData);
  const [packagesState, setPackagesState] = useState(packages);
  const [tabsState, setTabsState] = useState(tabs);
  const [activeTab, setActiveTab] = useState(0);
  const [coverImageFile, setCoverImageFile] = useState(null);

  // Background configuration for the editable card on this page,
  // initialised from the event's stored background array when available.
  const initialBgArray = event?.background_images_array || event?.background || null;
  let initialMode = 'solid';
  let initialColor1 = '#f0f0f0';
  let initialColor2 = '#000000';
  let initialImageUrl = '';

  if (Array.isArray(initialBgArray) && initialBgArray.length > 0) {
    const mode = initialBgArray[0];
    const value1 = initialBgArray[1];
    const value2 = initialBgArray[2];

    if (mode === 'solid' && value1) {
      initialMode = 'solid';
      initialColor1 = value1;
    } else if (mode === 'gradient' && value1 && value2) {
      initialMode = 'gradient';
      initialColor1 = value1;
      initialColor2 = value2;
    } else if (mode === 'image' && value1) {
      initialMode = 'image';
      initialImageUrl = value1;
    }
  }

  const [backgroundMode, setBackgroundMode] = useState(initialMode);
  const [imageBgColor, setImageBgColor] = useState(initialColor1);
  const [imageBgColor2, setImageBgColor2] = useState(initialColor2);
  const [bgImageUrl, setBgImageUrl] = useState(initialImageUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessages, setValidationMessages] = useState({
    information: [],
    packages: [],
    terms: [],
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // When a different event is opened, reset local state from the memoised data
  useEffect(() => {
    console.log('[RehostEventDetails] 📋 Event loaded from route state:', {
      columns: 'event_name, location, start_date/date/startdate, end_date/enddate, start_time/time, end_time, event_type/type, capacity/total_tickets_planned, description, terms_and_conditions, cover_image_url/file_url, background_images_array/background, packages[{name,price,max_tickets,...}], tab_sections[{name,content}]',
      mappedFormData: formData,
      packageCount: packages?.length,
      tabCount: tabs?.length,
    });
    setFormState(formData);
    setPackagesState(packages);
    setTabsState(tabs);
    setActiveTab(0);
    setCoverImageFile(null);
  }, [formData, packages, tabs]);

  // Handlers to make the shell editable (no submission logic here)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  // LocationPicker returns an object; extract a display string wherever needed
  const getLocationString = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return loc.formatted_address || loc.street_address || '';
  };

  // Rehost page event type options (keep in sync with organiser event form)
  const eventTypeOptions = [
    'Conference',
    'Workshop',
    'Seminar',
    'Exhibition',
    'Concert',
    'Festival',
    'Corporate Event',
    'Sports Event',
    'Other',
  ];

  const handleEventTypeChange = (e) => {
    const value = e.target.value;
    setFormState((prev) => ({ ...prev, type: value }));
  };

  const computeDuration = (startDate, endDate, startTime, endTime) => {
    try {
      if (!startDate || !endDate || !startTime || !endTime) return '';
      const start = new Date(`${startDate}T${startTime}`);
      const end = new Date(`${endDate}T${endTime}`);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
      const diffMs = end - start;
      if (diffMs < 0) return '';
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}h ${minutes}m`;
    } catch (error) {
      console.error('Error computing duration for rehost:', error);
      return '';
    }
  };

  const handlePackageChange = (index, field, value) => {
    setPackagesState((prev) => {
      const updated = [...prev];
      if (!updated[index]) updated[index] = {};
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Cover image change handler so uploads are previewed in the shell
  const handleCoverImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImageFile(file);
    }
  };

  const collectMissingFields = () => {
    const missing = {
      information: [],
      packages: [],
      terms: [],
    };

    // Information section: ensure key fields are provided
    if (!formState.name || !formState.name.trim()) {
      missing.information.push('Event name');
    }
    if (!getLocationString(formState.location)) {
      missing.information.push('Location');
    }
    if (!formState.startdate) {
      missing.information.push('Event date');
    }
    if (!formState.start_time) {
      missing.information.push('Event time');
    }
    if (!formState.type || !formState.type.trim()) {
      missing.information.push('Event type');
    }

    // Packages section: ensure each visible package is fully specified
    if (!packagesState || packagesState.length === 0) {
      missing.packages.push('At least one package');
    } else {
      packagesState.forEach((pkg, index) => {
        const labelPrefix = `Package ${index + 1}: `;
        if (!pkg.packageType || !pkg.packageType.trim()) {
          missing.packages.push(`${labelPrefix}Package type`);
        }
        if (pkg.numberOfTickets == null || pkg.numberOfTickets === '' || Number(pkg.numberOfTickets) <= 0) {
          missing.packages.push(`${labelPrefix}Tickets count`);
        }
        if (!pkg.startDate) {
          missing.packages.push(`${labelPrefix}Start date`);
        }
        if (!pkg.endDate) {
          missing.packages.push(`${labelPrefix}End date`);
        }
        if (!pkg.pricing || String(pkg.pricing).trim() === '') {
          missing.packages.push(`${labelPrefix}Price`);
        }
      });
    }

    // Terms section
    if (!formState.terms_and_conditions || !formState.terms_and_conditions.trim()) {
      missing.terms.push('Terms and Conditions');
    }

    return missing;
  };

  const handleSubmitRehost = async () => {
    if (isSubmitting) return;

    const missing = collectMissingFields();
    const hasMissing =
      (missing.information && missing.information.length > 0) ||
      (missing.packages && missing.packages.length > 0) ||
      (missing.terms && missing.terms.length > 0);

    if (hasMissing) {
      setValidationMessages(missing);
      setShowValidationModal(true);
      return;
    }

    try {
      setIsSubmitting(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setIsSubmitting(false);
        navigate('/');
        return;
      }

      const duration = computeDuration(
        formState.startdate,
        formState.enddate || formState.startdate,
        formState.start_time,
        formState.end_time || formState.start_time
      );

      const formDataToSend = new FormData();
      formDataToSend.append('name', formState.name || '');
      formDataToSend.append('location', getLocationString(formState.location));
      formDataToSend.append('startdate', formState.startdate || '');
      formDataToSend.append('enddate', formState.enddate || formState.startdate || '');
      formDataToSend.append('time', formState.start_time || '');
      formDataToSend.append('endtime', formState.end_time || formState.start_time || '');
      formDataToSend.append('deadlinedate', formState.deadlinedate || formState.startdate || '');
      formDataToSend.append('deadlinetime', formState.deadlinetime || formState.start_time || '');
      formDataToSend.append('type', formState.type || '');
      formDataToSend.append('capacity', String(formState.capacity || 1));
      formDataToSend.append('description', formState.description || '');
      formDataToSend.append('terms_and_conditions', formState.terms_and_conditions || '');
      formDataToSend.append('duration', duration || '');

      const attendeesData = JSON.stringify([]); // no attendees on rehost shell
      const tabsData = JSON.stringify(Array.isArray(tabsState) ? tabsState : []);
      const packagesData = JSON.stringify(Array.isArray(packagesState) ? packagesState : []);

      formDataToSend.append('attendees', attendeesData);
      formDataToSend.append('tabs', tabsData);
      formDataToSend.append('packages', packagesData);

      // Background configuration for CoverImages.Background
      formDataToSend.append('backgroundMode', backgroundMode);
      formDataToSend.append('imageBgColor', imageBgColor);
      formDataToSend.append('imageBgColor2', imageBgColor2);
      formDataToSend.append('bgImageUrl', bgImageUrl);

      if (coverImageFile) {
        formDataToSend.append('cover_image', coverImageFile);
      }

      console.log('[RehostEventDetails] 📤 Submitting rehost — API: POST /organiser/events', {
        name: formState.name,
        startdate: formState.startdate,
        enddate: formState.enddate,
        packageCount: packagesState?.length,
      });
      const response = await fetch(`${API_BASE_URL}/organiser/events`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || result.details || 'Failed to submit rehost request');
      }

      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        navigate('/organiser-dash');
      }, 3000);
    } catch (error) {
      console.error('Rehost submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addPackage = () => {
    setPackagesState((prev) => [
      ...prev,
      {
        selectType: 'Full Package',
        packageType: '',
        location: getLocationString(formState.location),
        duration: '',
        startDate: '',
        endDate: '',
        pricing: '',
        details: '',
        typeOptions: ['Full Package', 'Day'],
      },
    ]);
  };

  const removePackage = (indexToRemove) => {
    setPackagesState((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const addTab = () => {
    setTabsState((prev) => [...prev, { name: '', content: '' }]);
  };

  const removeTab = (indexToRemove) => {
    setTabsState((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleTabChange = (index, field, value) => {
    setTabsState((prev) => {
      const updated = [...prev];
      if (!updated[index]) updated[index] = { name: '', content: '' };
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  return (
    <div className="container12">
      <GlobalHeader
        title={headerTitle}
        subtitle="Review event details and choose new dates"
        backTo="/rehost-event"
        logoutTo="/"
        onLogout={() => {
          localStorage.removeItem("token");
          localStorage.removeItem("userId");
          localStorage.removeItem("user");
          navigate("/");
        }}
      />

      <div className="main-content rehost-view-main">
        <div className="rehost-view-card">
          <EditableCustomerEventCard
            formData={formState}
            tabs={tabsState}
            packages={packagesState}
            coverImageFile={coverImageFile}
            activeTab={activeTab}
            onSelectTab={setActiveTab}
            handleInputChange={handleInputChange}
            handlePackageChange={handlePackageChange}
            addPackage={addPackage}
            addTab={addTab}
            handleTabChange={handleTabChange}
            removePackage={removePackage}
            removeTab={removeTab}
            onImageChange={handleCoverImageChange}
            eventTypeOptions={eventTypeOptions}
            handleEventTypeChange={handleEventTypeChange}
            customEventType=""
            handleCustomEventTypeChange={() => {}}
            // Use the existing event cover image (if any) as the initial placeholder
            placeholderCoverUrl={event?.cover_image_url || event?.file_url || '/fallback-logo.png'}
            backgroundMode={backgroundMode}
            setBackgroundMode={setBackgroundMode}
            imageBgColor={imageBgColor}
            setImageBgColor={setImageBgColor}
            imageBgColor2={imageBgColor2}
            setImageBgColor2={setImageBgColor2}
            bgImageUrl={bgImageUrl}
            setBgImageUrl={setBgImageUrl}
          />
        </div>
      </div>

      {/* Bottom page actions */}
      <div style={{ maxWidth: '1040px', margin: '0.5rem auto 6rem', padding: '0 1rem' }}>
        <button
          type="button"
          className="submit-btn"
          onClick={handleSubmitRehost}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {showValidationModal && (
        <RehostModal
          title="Please fill in the following:"
          onClose={() => setShowValidationModal(false)}
          showCloseButton
        >
          {validationMessages.information && validationMessages.information.length > 0 && (
            <div className="rehost-modal-section">
              <h4>Information</h4>
              <ul>
                {validationMessages.information.map((item, idx) => (
                  <li key={`info-${idx}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {validationMessages.packages && validationMessages.packages.length > 0 && (
            <div className="rehost-modal-section">
              <h4>Packages</h4>
              <ul>
                {validationMessages.packages.map((item, idx) => (
                  <li key={`pkg-${idx}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {validationMessages.terms && validationMessages.terms.length > 0 && (
            <div className="rehost-modal-section">
              <h4>Terms &amp; Conditions</h4>
              <ul>
                {validationMessages.terms.map((item, idx) => (
                  <li key={`terms-${idx}`}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </RehostModal>
      )}
    </div>
  );
};

export default RehostEventDetails;
